import { FC, useState } from 'react';
import { Grid, Card, CardContent, Typography, Box, Avatar, Divider, CircularProgress, Alert, IconButton } from '@mui/material';
import { Person, School, Badge, AccountBalance, Edit } from '@mui/icons-material';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import useGuardianProfileService from '../../../../../../apis/guardian/sub-apis/useGuardianProfileService';
import { GENDER } from '../../../../../../apis/users/users.interfaces';
import { IBankDetails } from '../../../../../../apis/staff/staffPayout.interfaces';
import StructuredModal from '../../../../../components/structuredModal/structuredModal';
import ControlledTextField from '../../../../../components/controlledComponents/ControlledTextField.component';

interface GuardianProfileInfoTabProps {
  studentId: string;
}

const GuardianProfileInfoTab: FC<GuardianProfileInfoTabProps> = ({ studentId }) => {
  const { translateText } = useTranslation();
  const { getProfileData, updateBankDetails } = useGuardianProfileService();
  const queryClient = useQueryClient();
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['guardian-profile', studentId],
    queryFn: () => getProfileData(studentId),
    enabled: !!studentId,
  });

  const bankDetailsSchema = z.object({
    accountNumber: z.string().min(1, translateText('guardian.profile.info.validation.accountNumberRequired')),
    ifscCode: z.string().min(1, translateText('guardian.profile.info.validation.ifscCodeRequired')),
    bankName: z.string().min(1, translateText('guardian.profile.info.validation.bankNameRequired')),
    branchName: z.string().optional(),
  });

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm<IBankDetails>({
    resolver: zodResolver(bankDetailsSchema),
    defaultValues: {
      accountNumber: '',
      ifscCode: '',
      bankName: '',
      branchName: '',
    },
  });

  const updateBankDetailsMutation = useMutation({
    mutationFn: (bankDetails: IBankDetails) => updateBankDetails(studentId, bankDetails),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guardian-profile', studentId] });
      setIsEditDialogOpen(false);
    },
  });

  const handleEditClick = () => {
    if (data?.student.bankDetails) {
      reset(data.student.bankDetails);
    }
    setIsEditDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsEditDialogOpen(false);
    reset();
  };

  const onSubmit = (formData: IBankDetails) => {
    updateBankDetailsMutation.mutate(formData);
  };

  if (!studentId) {
    return (
      <Alert severity="info" data-testid="guardian-profile-info-no-student">
        {translateText('guardian.selectChild')}
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh" data-testid="guardian-profile-info-loading">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Alert severity="error" data-testid="guardian-profile-info-error">
        {translateText('common.error')}
      </Alert>
    );
  }

  const { student, guardians, extraGuardians } = data;
  const studentName = `${student.basicDetails.firstName} ${student.basicDetails.middleName || ''} ${student.basicDetails.lastName}`.trim();
  const admissionDate = student.admissionDate?.timestamp
    ? new Date(student.admissionDate.timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
    : translateText('common.nA');

  const getAcademicYear = (admissionTimestamp?: number): string => {
    if (!admissionTimestamp) return translateText('common.nA');
    const admissionDate = new Date(admissionTimestamp * 1000);
    const year = admissionDate.getFullYear();
    const month = admissionDate.getMonth();
    // Academic year typically runs from April to March
    if (month >= 3) {
      return `${year}-${year + 1}`;
    } else {
      return `${year - 1}-${year}`;
    }
  };

  const academicYear = getAcademicYear(student.admissionDate?.timestamp);

  const formatPhone = (phone: { countryCode: string; phoneNumber: string }) => {
    return `${phone.countryCode} ${phone.phoneNumber}`;
  };

  const formatAddress = (address?: {
    address1: string;
    address2?: string | null;
    city: string;
    state?: string | null;
    country?: string | null;
    postalCode: string;
  }) => {
    if (!address) return translateText('common.nA');
    const parts = [
      address.address1,
      address.address2,
      address.city,
      address.state,
      address.country,
      address.postalCode,
    ].filter(Boolean);
    return parts.join(', ');
  };

  return (
    <Grid container spacing={3} data-testid="guardian-profile-info-container">
      {/* Student Profile Section */}
      <Grid item xs={12} data-testid="guardian-profile-student-profile-section">
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" gap={2} mb={3}>
              <School color="primary" />
              <Typography variant="h6">
                {translateText('guardian.profile.info.studentProfile')}
              </Typography>
              <Typography
                variant="body1"
                fontWeight={600}
                color="text.secondary"
                data-testid="guardian-profile-student-id-value"
              >
                {translateText('guardian.profile.info.studentId')}: {student.id}
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                  <Avatar sx={{ width: 120, height: 120 }}>
                    <Person sx={{ fontSize: 60 }} />
                  </Avatar>
                  <Typography variant="h6" textAlign="center" data-testid="guardian-profile-student-name-value">
                    {studentName}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={9}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      {translateText('guardian.profile.info.grade')}
                    </Typography>
                    <Typography variant="body1" fontWeight={600} data-testid="guardian-profile-grade-value">
                      {student.grade || translateText('common.nA')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      {translateText('guardian.profile.info.section')}
                    </Typography>
                    <Typography variant="body1" fontWeight={600} data-testid="guardian-profile-section-value">
                      {student.section || translateText('common.nA')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      {translateText('guardian.profile.info.gender')}
                    </Typography>
                    <Typography variant="body1" fontWeight={600} data-testid="guardian-profile-gender-value">
                      {student.basicDetails.gender === GENDER.MALE
                        ? translateText('common.male')
                        : student.basicDetails.gender === GENDER.FEMALE
                          ? translateText('common.female')
                          : translateText('common.other')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      {translateText('guardian.profile.info.email')}
                    </Typography>
                    <Typography variant="body1" data-testid="guardian-profile-email-value">
                      {student.contactDetails.email?.email || translateText('common.nA')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      {translateText('guardian.profile.info.phone')}
                    </Typography>
                    <Typography variant="body1" data-testid="guardian-profile-phone-value">
                      {student.contactDetails.phone
                        ? formatPhone(student.contactDetails.phone)
                        : translateText('common.nA')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      {translateText('guardian.profile.info.address')}
                    </Typography>
                    <Typography variant="body1" data-testid="guardian-profile-address-value">
                      {formatAddress(student.contactDetails.address || undefined)}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Admission Details Section */}
      <Grid item xs={12} data-testid="guardian-profile-admission-details-section">
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" gap={2} mb={3}>
              <Badge color="primary" />
              <Typography variant="h6">
                {translateText('guardian.profile.info.admissionDetails')}
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  {translateText('guardian.profile.info.admissionNumber')}
                </Typography>
                <Typography variant="body1" fontWeight={600} data-testid="guardian-profile-admission-number-value">
                  {student.id || translateText('common.nA')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  {translateText('guardian.profile.info.admissionDate')}
                </Typography>
                <Typography variant="body1" fontWeight={600} data-testid="guardian-profile-admission-date-value">
                  {admissionDate}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  {translateText('guardian.profile.info.academicYear')}
                </Typography>
                <Typography variant="body1" fontWeight={600} data-testid="guardian-profile-academic-year-value">
                  {academicYear}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  {translateText('guardian.profile.info.category')}
                </Typography>
                <Typography variant="body1" fontWeight={600} data-testid="guardian-profile-category-value">
                  {student.category || translateText('common.nA')}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Bank Details Section */}
      <Grid item xs={12} data-testid="guardian-profile-bank-details-section">
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
              <Box display="flex" alignItems="center" gap={2}>
                <AccountBalance color="primary" />
                <Typography variant="h6">
                  {translateText('guardian.profile.info.bankDetails')}
                </Typography>
              </Box>
              {student.bankDetails && (
                <IconButton
                  onClick={handleEditClick}
                  color="primary"
                  size="small"
                  data-testid="guardian-profile-bank-details-edit-button"
                >
                  <Edit />
                </IconButton>
              )}
            </Box>
            <Divider sx={{ mb: 3 }} />
            {student.bankDetails ? (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    {translateText('guardian.profile.info.accountNumber')}
                  </Typography>
                  <Typography variant="body1" fontWeight={600} data-testid="guardian-profile-account-number-value">
                    {student.bankDetails.accountNumber || translateText('common.nA')}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    {translateText('guardian.profile.info.ifscCode')}
                  </Typography>
                  <Typography variant="body1" fontWeight={600} data-testid="guardian-profile-ifsc-code-value">
                    {student.bankDetails.ifscCode || translateText('common.nA')}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    {translateText('guardian.profile.info.bankName')}
                  </Typography>
                  <Typography variant="body1" fontWeight={600} data-testid="guardian-profile-bank-name-value">
                    {student.bankDetails.bankName || translateText('common.nA')}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    {translateText('guardian.profile.info.branchName')}
                  </Typography>
                  <Typography variant="body1" fontWeight={600} data-testid="guardian-profile-branch-name-value">
                    {student.bankDetails.branchName || translateText('common.nA')}
                  </Typography>
                </Grid>
              </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary" data-testid="guardian-profile-no-bank-details">
                {translateText('guardian.profile.info.noBankDetails')}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Edit Bank Details Dialog */}
      <StructuredModal
        isOpen={isEditDialogOpen}
        onClose={handleCloseDialog}
        title={translateText('guardian.profile.info.editBankDetails')}
        footerPositiveAction={handleSubmit(onSubmit)}
        footerPositiveActionText={translateText('common.save')}
        footerNegativeAction={handleCloseDialog}
        footerNegativeActionText={translateText('common.cancel')}
        footerMessage={
          updateBankDetailsMutation.isError ? (
            <Typography color="error" variant="body2">
              {translateText('guardian.profile.info.updateError')}
            </Typography>
          ) : null
        }
        isLoading={updateBankDetailsMutation.isPending}
      >
        <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }}>
          <Grid container spacing={2} sx={{ pt: 2 }}>
            <Grid item xs={12}>
              <ControlledTextField
                control={control}
                name="accountNumber"
                id="account-number"
                label={translateText('guardian.profile.info.accountNumber')}
                errorMessage={errors.accountNumber?.message}
                isGridItem={false}
                data-testid="bank-details-account-number"
              />
            </Grid>
            <Grid item xs={12}>
              <ControlledTextField
                control={control}
                name="ifscCode"
                id="ifsc-code"
                label={translateText('guardian.profile.info.ifscCode')}
                errorMessage={errors.ifscCode?.message}
                isGridItem={false}
                data-testid="bank-details-ifsc-code"
              />
            </Grid>
            <Grid item xs={12}>
              <ControlledTextField
                control={control}
                name="bankName"
                id="bank-name"
                label={translateText('guardian.profile.info.bankName')}
                errorMessage={errors.bankName?.message}
                isGridItem={false}
                data-testid="bank-details-bank-name"
              />
            </Grid>
            <Grid item xs={12}>
              <ControlledTextField
                control={control}
                name="branchName"
                id="branch-name"
                label={translateText('guardian.profile.info.branchName')}
                errorMessage={errors.branchName?.message}
                isGridItem={false}
                data-testid="bank-details-branch-name"
              />
            </Grid>
          </Grid>
        </form>
      </StructuredModal>

      {/* Guardian Profile Section */}
      <Grid item xs={12} data-testid="guardian-profile-guardian-profile-section">
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" gap={2} mb={3}>
              <Person color="primary" />
              <Typography variant="h6">
                {translateText('guardian.profile.info.guardianProfile')}
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />
            {guardians.length === 0 && extraGuardians.length === 0 ? (
              <Typography variant="body2" color="text.secondary" data-testid="guardian-profile-no-guardians">
                {translateText('guardian.profile.info.noGuardians')}
              </Typography>
            ) : (
              <Grid container spacing={3}>
                {guardians.map((guardian, index) => {
                  const guardianName = `${guardian.basicDetails.firstName} ${guardian.basicDetails.middleName || ''} ${guardian.basicDetails.lastName}`.trim();
                  const relationship = index === 0
                    ? translateText('guardian.profile.info.father')
                    : translateText('guardian.profile.info.mother');

                  return (
                    <Grid item xs={12} md={6} key={guardian.id} data-testid={`guardian-profile-guardian-card-${index}`}>
                      <Card variant="outlined">
                        <CardContent>
                          <Box display="flex" alignItems="center" gap={2} mb={2}>
                            <Avatar>
                              <Person />
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle1" fontWeight={600} data-testid={`guardian-profile-guardian-name-value-${index}`}>
                                {guardianName}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" data-testid={`guardian-profile-guardian-relationship-value-${index}`}>
                                {relationship}
                              </Typography>
                            </Box>
                          </Box>
                          <Grid container spacing={1}>
                            <Grid item xs={12}>
                              <Typography variant="body2" color="text.secondary">
                                {translateText('guardian.profile.info.email')}
                              </Typography>
                              <Typography variant="body2" data-testid={`guardian-profile-guardian-email-value-${index}`}>
                                {guardian.contactDetails.email?.email || translateText('common.nA')}
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Typography variant="body2" color="text.secondary">
                                {translateText('guardian.profile.info.phone')}
                              </Typography>
                              <Typography variant="body2" data-testid={`guardian-profile-guardian-phone-value-${index}`}>
                                {guardian.contactDetails.phone
                                  ? formatPhone(guardian.contactDetails.phone)
                                  : translateText('common.nA')}
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Typography variant="body2" color="text.secondary">
                                {translateText('guardian.profile.info.address')}
                              </Typography>
                              <Typography variant="body2" data-testid={`guardian-profile-guardian-address-value-${index}`}>
                                {formatAddress(guardian.contactDetails.address || undefined)}
                              </Typography>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}

                {extraGuardians.map((guardian, extraIndex) => {
                  const guardianName = `${guardian.basicDetails.firstName} ${guardian.basicDetails.middleName || ''} ${guardian.basicDetails.lastName}`.trim();
                  const relationship = guardian.basicDetails.emergencyContact?.relationship || translateText('guardian.profile.info.extraGuardian');

                  return (
                    <Grid item xs={12} md={6} key={guardian.id} data-testid={`guardian-profile-extra-guardian-card-${extraIndex}`}>
                      <Card variant="outlined">
                        <CardContent>
                          <Box display="flex" alignItems="center" gap={2} mb={2}>
                            <Avatar>
                              <Person />
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle1" fontWeight={600} data-testid={`guardian-profile-extra-guardian-name-value-${extraIndex}`}>
                                {guardianName}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" data-testid={`guardian-profile-extra-guardian-relationship-value-${extraIndex}`}>
                                {relationship}
                              </Typography>
                            </Box>
                          </Box>
                          <Grid container spacing={1}>
                            <Grid item xs={12}>
                              <Typography variant="body2" color="text.secondary">
                                {translateText('guardian.profile.info.email')}
                              </Typography>
                              <Typography variant="body2" data-testid={`guardian-profile-extra-guardian-email-value-${extraIndex}`}>
                                {guardian.contactDetails.email?.email || translateText('common.nA')}
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Typography variant="body2" color="text.secondary">
                                {translateText('guardian.profile.info.phone')}
                              </Typography>
                              <Typography variant="body2" data-testid={`guardian-profile-extra-guardian-phone-value-${extraIndex}`}>
                                {guardian.contactDetails.phone
                                  ? formatPhone(guardian.contactDetails.phone)
                                  : translateText('common.nA')}
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Typography variant="body2" color="text.secondary">
                                {translateText('guardian.profile.info.address')}
                              </Typography>
                              <Typography variant="body2" data-testid={`guardian-profile-extra-guardian-address-value-${extraIndex}`}>
                                {formatAddress(guardian.contactDetails.address || undefined)}
                              </Typography>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default GuardianProfileInfoTab;
